import { useEffect } from "react";

// react-helmet-async 2.0.5 e' risultato completamente inerte in questo progetto
// (dev e produzione): nessun tag iniettato, quindi ogni pagina serviva title,
// description e canonical della home. Il pacchetto non e' piu' mantenuto, quindi
// invece di inseguire la versione giusta il <head> lo gestiamo qui, a mano.
//
// Regola: i tag che esistono gia' in index.html vengono SOVRASCRITTI e ripristinati
// allo smontaggio (niente duplicati di description/og:*), quelli nuovi vengono
// creati e rimossi. I JSON-LD sono sempre creati ex novo perche' possono coesistere.

export type HeadTag =
  | { tag: "title"; text: string }
  | { tag: "meta"; name: string; content: string }
  | { tag: "meta"; property: string; content: string }
  | { tag: "link"; rel: string; href: string }
  | { tag: "ldjson"; data: unknown };

const selectorFor = (t: HeadTag): string | null => {
  if (t.tag === "meta") {
    return "name" in t ? `meta[name="${t.name}"]` : `meta[property="${t.property}"]`;
  }
  if (t.tag === "link") return `link[rel="${t.rel}"]`;
  return null;
};

export function useHead(scope: string, tags: HeadTag[]) {
  // le tag sono ricostruite a ogni render: la firma serializzata evita di
  // rimontare il <head> a ogni giro senza costringere i chiamanti a useMemo
  const signature = JSON.stringify(tags);

  useEffect(() => {
    const parsed: HeadTag[] = JSON.parse(signature);
    const created: Element[] = [];
    const restored: Array<() => void> = [];

    for (const t of parsed) {
      if (t.tag === "title") {
        const before = document.title;
        document.title = t.text;
        restored.push(() => {
          document.title = before;
        });
        continue;
      }

      if (t.tag === "ldjson") {
        const el = document.createElement("script");
        el.setAttribute("type", "application/ld+json");
        el.setAttribute("data-head", scope);
        el.textContent = JSON.stringify(t.data);
        document.head.appendChild(el);
        created.push(el);
        continue;
      }

      const attr = t.tag === "link" ? "href" : "content";
      const value = t.tag === "link" ? t.href : t.content;
      const existing = document.head.querySelector(selectorFor(t)!);

      if (existing) {
        const before = existing.getAttribute(attr);
        existing.setAttribute(attr, value);
        restored.push(() => {
          if (before === null) existing.removeAttribute(attr);
          else existing.setAttribute(attr, before);
        });
        continue;
      }

      const el = document.createElement(t.tag);
      if (t.tag === "link") el.setAttribute("rel", t.rel);
      else if ("name" in t) el.setAttribute("name", t.name);
      else el.setAttribute("property", t.property);
      el.setAttribute(attr, value);
      el.setAttribute("data-head", scope);
      document.head.appendChild(el);
      created.push(el);
    }

    return () => {
      created.forEach((el) => el.remove());
      restored.forEach((undo) => undo());
    };
  }, [scope, signature]);
}
